import { gql } from '@apollo/client'
import { StateAliases } from '@helo/cms'

import { SavedView } from '../store/appStore/view/viewSlice.types'
import { dateToDateString } from '../utils/dates'
import { stripTableConfigMetadata } from '../utils/tableConfigUtils'
import { apolloClient } from './apolloClient'

const normalizeLoadedSavedView = (
  savedView: SavedView | null,
): SavedView | null => {
  if (!savedView) return null
  return StateAliases.normalizeSavedViewPayload(savedView)
}

const savedViewDocument = gql`
  query SavedView($id: String!) {
    savedView(id: $id) {
      id
      name
      description
      viewId
      viewState
      tableConfig
      createdDate
      updatedDate
      userId
      organizationId
    }
  }
`

export const savedViewsListQuery = gql`
  query SavedViews($first: Int, $skip: Int) {
    SavedView_list(
      first: $first
      skip: $skip
      orderBy: [{ createdDate: DESC }]
    ) {
      items {
        id
        name
        description
        viewId
        viewState
        tableConfig
        createdDate
        updatedDate
        userId
        organizationId
      }
    }
  }
`

const createSavedViewDocument = gql`
  mutation CreateSavedView($input: CreateSavedViewInput!) {
    createSavedView(input: $input) {
      id
      name
      description
      viewId
      viewState
      tableConfig
      createdDate
      updatedDate
      userId
      organizationId
    }
  }
`

const updateSavedViewDocument = gql`
  mutation UpdateSavedView($input: UpdateSavedViewInput!) {
    updateSavedView(input: $input) {
      id
      name
      description
      viewId
      viewState
      tableConfig
      createdDate
      updatedDate
      userId
      organizationId
    }
  }
`

const deleteSavedViewDocument = gql`
  mutation DeleteSavedView($id: String!) {
    deleteSavedView(id: $id)
  }
`

export const savedViewService = {
  getSavedView: async (id: string): Promise<SavedView | null> => {
    const result = await apolloClient.query({
      query: savedViewDocument,
      variables: { id },
    })

    return normalizeLoadedSavedView(
      ((result.data as any)?.savedView ?? null) as SavedView | null,
    )
  },

  saveView: async (input: Partial<SavedView>): Promise<SavedView> => {
    try {
      const canonicalInput = StateAliases.normalizeSavedViewPayload(input)
      const inputWithStrippedTableConfig = {
        ...canonicalInput,
        ...(canonicalInput.tableConfig != null && {
          tableConfig: stripTableConfigMetadata(canonicalInput.tableConfig),
        }),
      }
      const result = await apolloClient.mutate({
        mutation: createSavedViewDocument,
        variables: { input: inputWithStrippedTableConfig as any },
      })

      if (!(result.data as any)?.createSavedView) {
        throw new Error('Failed to create saved view')
      }

      return normalizeLoadedSavedView(
        (result.data as any).createSavedView as SavedView,
      ) as SavedView
    } catch (error) {
      throw error
    }
  },

  updateView: async (
    view: Partial<SavedView> & { id: string },
  ): Promise<SavedView> => {
    try {
      if (!view.id) {
        throw new Error('View ID is required for update')
      }

      const canonicalView = StateAliases.normalizeSavedViewPayload(view)
      const input = {
        ...canonicalView,
        ...(canonicalView.tableConfig != null && {
          tableConfig: stripTableConfigMetadata(canonicalView.tableConfig),
        }),
      }

      const result = await apolloClient.mutate({
        mutation: updateSavedViewDocument,
        variables: { input: input as any },
      })

      if (!(result.data as any)?.updateSavedView) {
        throw new Error('Failed to update saved view')
      }

      return normalizeLoadedSavedView(
        (result.data as any).updateSavedView as SavedView,
      ) as SavedView
    } catch (error) {
      throw error
    }
  },

  fetchView: async (id: string): Promise<SavedView | null> => {
    try {
      const result = await apolloClient.query({
        query: savedViewDocument,
        variables: { id },
      })

      if (!(result.data as any)?.savedView) {
        return null
      }

      return normalizeLoadedSavedView(
        (result.data as any).savedView as SavedView,
      )
    } catch (error) {
      throw error
    }
  },

  fetchViewByName: async (viewName: string): Promise<SavedView | null> => {
    try {
      const views = await savedViewService.fetchViews()
      return views.find(view => view.name === viewName) ?? null
    } catch (error) {
      throw error
    }
  },

  fetchViews: async (): Promise<SavedView[]> => {
    try {
      const result = await apolloClient.query({
        query: savedViewsListQuery,
        variables: {
          first: 500,
          skip: 0,
        },
        fetchPolicy: 'network-only',
      })

      if (!(result.data as any)?.SavedView_list?.items) {
        return []
      }

      return ((result.data as any).SavedView_list.items as SavedView[]).map(
        view => StateAliases.normalizeSavedViewPayload(view),
      )
    } catch (error) {
      throw error
    }
  },

  deleteView: async (id: string): Promise<void> => {
    try {
      await apolloClient.mutate({
        mutation: deleteSavedViewDocument,
        variables: { id },
      })
    } catch (error) {
      throw error
    }
  },

  deleteViewByName: async (viewName: string): Promise<void> => {
    try {
      const view = await savedViewService.fetchViewByName(viewName)
      if (view?.id) {
        await savedViewService.deleteView(view.id)
      }
    } catch (error) {
      throw error
    }
  },
}

export const createSavedView = (savedView: Partial<SavedView>) => ({
  name: 'no_name',
  viewId: 'no_view_id',
  createdAt: dateToDateString(new Date()),
  updatedAt: dateToDateString(new Date()),
  description: '',
  queryStates: [],
  widgetStates: [],
  ...savedView,
})
